import React, { useEffect, useState } from "react";
import EkPed3CreateForm from "./EkPed3CreateForm";
import useBackdropClose from "./useBackdropClose";
import bg from "../../assets/images/ekped3-popup-bg.png";
import "./EkPed3Modal.css";

/*
 * Ek Ped Maa Ke Naam 3.0 — post-login workflow.
 *
 * School has NO 3.0 record yet (auto-shown once per login/reload by the caller):
 *   Popup "ask"       "Have you participated in Ek Ped Maa Ke Naam 3.0?"
 *                       Yes → Create form (record the plantation)
 *                       No  → "encourage" popup → OK → Dashboard
 *
 * School ALREADY has 3.0 data (never auto-shown; opened only via the dashboard's
 * "Update Ek Ped Maa Ke Naam 3.0 Details" button):
 *   Popup "updateAsk" "Do you want to update the Ek Ped Maa Ke Naam 3.0 plantation count?"
 *                       Yes → form (prefilled, adds another entry, ≤ 700 total)
 *                       No  → Dashboard
 *
 * On success the caller re-checks so the running total stays accurate.
 */

// A sprouting seedling
const Seedling = () => (
  <svg className="ekped3-sprout" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M32 58V30" stroke="#2f8f4a" strokeWidth="4" strokeLinecap="round" />
    <path d="M32 34c-2-9-9-13-18-13 0 10 7 15 18 15Z" fill="#43b563" />
    <path d="M32 30c2-10 10-14 20-13 0 11-9 16-20 15Z" fill="#63cf7f" />
    <path d="M20 58h24c0-3-5-5-12-5s-12 2-12 5Z" fill="#7a4a25" />
    <ellipse cx="32" cy="57" rx="14" ry="3" fill="#8a5a2f" />
  </svg>
);

// A full-grown tree
const GrownTree = () => (
  <svg className="ekped3-sprout" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M32 60V38" stroke="#7a4a25" strokeWidth="5" strokeLinecap="round" />
    <circle cx="32" cy="26" r="18" fill="#43b563" />
    <circle cx="20" cy="32" r="12" fill="#5bc177" />
    <circle cx="44" cy="32" r="12" fill="#5bc177" />
    <circle cx="32" cy="22" r="13" fill="#6ed08a" />
  </svg>
);

const DecisionCard = ({ children }) => (
  <div className="ekped3-decision" style={{ backgroundImage: `url(${bg})` }}>
    <div className="ekped3-decision-body">{children}</div>
  </div>
);

export default function EkPed3Modal({
  isOpen,
  onClose,
  onCompleted,
  exists = false,
  total = 0,
  cap = 700,
  record = null,
}) {
  const [step, setStep] = useState("ask"); // 'ask' | 'encourage' | 'updateAsk' | 'form'

  useEffect(() => {
    if (isOpen) setStep(exists ? "updateAsk" : "ask");
  }, [isOpen, exists]);

  // Clicking the dimmed area around the card closes the popup (same as "No" /
  // the dashboard link): dismissed for this session, off to the dashboard.
  const backdrop = useBackdropClose(onClose);

  if (!isOpen) return null;

  return (
    <div className="ekped3-overlay" role="dialog" aria-modal="true" aria-label="Ek Ped Maa Ke Naam 3.0" {...backdrop}>
      {/* No record yet — did you participate? */}
      {step === "ask" && (
        <DecisionCard>
          <Seedling />
          <h2 className="ekped3-decision-title">Ek Ped Maa Ke Naam 3.0</h2>
          <p className="ekped3-decision-q">Have you participated in Ek Ped Maa Ke Naam 3.0?</p>
          <div className="ekped3-btn-row">
            <button type="button" className="ekped3-btn ekped3-btn-yes" onClick={() => setStep("form")}>Yes</button>
            <button type="button" className="ekped3-btn ekped3-btn-no" onClick={() => setStep("encourage")}>No</button>
          </div>
        </DecisionCard>
      )}

      {/* Said "No" — encourage them to participate */}
      {step === "encourage" && (
        <DecisionCard>
          <Seedling />
          <h2 className="ekped3-decision-title">Ek Ped Maa Ke Naam 3.0</h2>
          <p className="ekped3-decision-q">
            Please participate in Ek Ped Maa Ke Naam 3.0 by planting trees and organising a
            plantation drive at your school. Once done, come back here to record your details.
          </p>
          <div className="ekped3-btn-row">
            <button type="button" className="ekped3-btn ekped3-btn-yes" onClick={onClose}>Got it, take me to Dashboard</button>
          </div>
          <button type="button" className="ekped3-back-link" onClick={() => setStep("ask")}>← Back</button>
        </DecisionCard>
      )}

      {/* Already has data — add / update the plantation count */}
      {step === "updateAsk" && (
        <DecisionCard>
          <GrownTree />
          <h2 className="ekped3-decision-title">Ek Ped Maa Ke Naam 3.0</h2>
          <p className="ekped3-decision-q">
            Do you want to update the Ek Ped Maa Ke Naam 3.0 plantation count?
            <br />
            <span className="ekped3-decision-note">
              Your school has recorded <b>{total}</b> saplings so far.
            </span>
          </p>
          <div className="ekped3-btn-row">
            <button type="button" className="ekped3-btn ekped3-btn-yes" onClick={() => setStep("form")}>Yes</button>
            <button type="button" className="ekped3-btn ekped3-btn-no" onClick={onClose}>No</button>
          </div>
        </DecisionCard>
      )}

      {/* The create / add-more form */}
      {step === "form" && (
        <div className="ekped3-form-modal" style={{ backgroundImage: `url(${bg}), linear-gradient(180deg,#f4f9ee,#eaf5e6)` }}>
          <EkPed3CreateForm
            mode={exists ? "update" : "create"}
            cap={cap}
            record={record}
            onBack={() => setStep(exists ? "updateAsk" : "ask")}
            onSuccess={onCompleted}
          />
        </div>
      )}
    </div>
  );
}
